import type { GraphQLFormattedError } from 'graphql';

import type { GraphQLOriginalError, GraphQLTranslationError } from './graphql.types';

type ErrorTranslator = (translationKey: string, params?: Record<string, unknown>) => string;

/**
 * `message` may be a plain string, an array (NestJS `BadRequestException(errorSink)`
 * bodies — see aurora-core-back's `sanitizeRecord`), or a bare object. Array entries
 * are themselves either plain strings (every handler except one) OR structured
 * `{ message, translation, params }` objects (aurora-core-back's `ArrayTypeHandler`,
 * enforce-required-many-to-many-relationships design.md D3). A structured entry's
 * `translation` key is resolved via `translator` when one is supplied; plain-string
 * entries and objects without a `translation` key are untouched (today's behavior).
 */
const normalizeErrorMessage = (message: unknown, translator?: ErrorTranslator): string => {
  if (typeof message === 'string') return message;
  if (Array.isArray(message)) {
    return message.map((entry) => normalizeErrorMessage(entry, translator)).join(', ');
  }
  if (message && typeof message === 'object') {
    const obj = message as Record<string, unknown>;
    const translationKey = obj['translation'];
    if (translator && typeof translationKey === 'string' && translationKey.length > 0) {
      const params = obj['params'] as Record<string, unknown> | undefined;
      const translated = translator(translationKey, params);
      // Transloco returns the key itself when no catalog entry exists — treat that
      // as "untranslatable" and fall through to the plain message instead.
      if (translated && translated !== translationKey) return translated;
    }
    // Standard NestJS message objects carry a 'message' key. If the key is absent
    // (e.g. RFC 6749 OAuth bodies), JSON.stringify is the last-resort fallback.
    // This path is NOT normally reached for OAuth errors because they are filtered out
    // via skipCodes BEFORE translation; it exists only for non-OAuth callers.
    return (obj['message'] as string) ?? JSON.stringify(message);
  }
  return String(message);
};

const hasOriginalError = (
  extensions: GraphQLFormattedError['extensions'],
): extensions is { originalError: GraphQLOriginalError } => {
  if (!extensions || typeof extensions !== 'object') return false;
  const ext = extensions as Record<string, unknown>;
  if (!ext['originalError'] || typeof ext['originalError'] !== 'object') return false;
  const original = ext['originalError'] as Record<string, unknown>;
  // statusCode must be present; message is optional (OAuth errors use `error` instead).
  return typeof original['statusCode'] === 'number';
};

/**
 * Extracts the RFC 6749 error code from a GraphQL error's `extensions.originalError`.
 *
 * Reads `originalError.error` directly — the shape produced by OAuth flow errors
 * after the backend includes `statusCode` in the body (post-finding-#1 fix).
 * Returns `undefined` when the path is missing or malformed.
 */
export const getOAuthErrorCode = (error: GraphQLFormattedError | undefined): string | undefined => {
  if (!error?.extensions) return undefined;
  const original = (error.extensions as Record<string, unknown>)['originalError'];
  if (!original || typeof original !== 'object') return undefined;
  const code = (original as Record<string, unknown>)['error'];
  return typeof code === 'string' ? code : undefined;
};

/**
 * Translates GraphQL errors into displayable messages, optionally skipping errors
 * whose RFC 6749 code is listed in `skipCodes`.
 *
 * Skipping happens BEFORE translation so suppressed errors never waste translation
 * work and the returned array aligns 1:1 with the errors that were NOT skipped
 * (eliminating index-drift bugs when multi-error responses are filtered later).
 *
 * @param graphqlErrors   Raw errors from a CombinedGraphQLErrors instance.
 * @param translator      Transloco translate function (key, params) → string.
 * @param skipCodes       RFC 6749 error codes to exclude from the result.
 */
export const extractGraphqlErrorTranslations = (
  graphqlErrors: ReadonlyArray<GraphQLFormattedError>,
  translator: ErrorTranslator,
  skipCodes?: readonly string[],
): GraphQLTranslationError[] => {
  return graphqlErrors
    .filter((error) => {
      // skipCodes only ever matches an `originalError`-bearing error (getOAuthErrorCode reads
      // that path) — errors without one fall through untouched.
      if (skipCodes && skipCodes.length > 0) {
        const code = getOAuthErrorCode(error);
        if (code !== undefined && skipCodes.includes(code)) return false;
      }
      return true;
    })
    .map((error) => {
      // T-2: an engine-level error (graphql-js non-null bubbling, syntax/validation) never
      // gets an `originalError` extension — that is only added by the backend's own exception
      // formatting. Dropping it here is how a real failure used to reach zero toasts; fall back
      // to the generic "SomethingWentWrong" key (global scope, already loaded eager) instead.
      if (!hasOriginalError(error.extensions)) {
        return { statusCode: 500, message: translator('SomethingWentWrong') };
      }
      const { originalError } = error.extensions as { originalError: GraphQLOriginalError };
      return {
        statusCode: originalError.statusCode,
        translation: originalError.translation,
        message: originalError.translation
          ? translator(originalError.translation, originalError.params)
          : normalizeErrorMessage(originalError.message, translator),
      };
    });
};
